import {
  Component,
  OnInit,
  Input,
  OnChanges,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { Store } from '@ngrx/store';
import { MeteringPointGroup, MeteringPoint, UserSettings, UserInfo } from '@model';
import { MeteringType, MeteringFilterType } from '@enums';
import { SxDialogService, SxSnackbarService } from '@services';
import { TranslateService } from '@ngx-translate/core';
import { MeteringGroupDialogComponent } from '@metering/components/metering-group-dialog/metering-group-dialog.component';

import * as fromMeteringStore from '@appStore/metering-points';
import * as fromAccountStore from '@appStore/account';
import { SubSink } from 'subsink';
import { filter } from 'rxjs/operators';
import { MeteringPointFormComponent } from '../metering-point-form/metering-point-form.component';

@Component({
    selector: 'app-metering-group-pane',
    templateUrl: './metering-group-pane.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MeteringGroupPaneComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  paneType: MeteringType;
  @Input()
  meteringPointGroups: MeteringPointGroup[];
  @Input()
  userSettings: UserSettings;
  @Input()
  userInfo:UserInfo;
  @Input()
  filterArea: string;

  meteringPointGroupsView: MeteringPointGroup[];
  selectedMPGId: number;
  isEditing: boolean;
  pristineGroup: MeteringPointGroup;

  private subs = new SubSink();
  
  constructor(
    private sxDialogService: SxDialogService,
    private sxSnackbarService: SxSnackbarService,
    private translate: TranslateService,
    private store: Store<fromMeteringStore.State>,
    private changeDetectorRef: ChangeDetectorRef,
    private accountStore: Store<fromAccountStore.AccountState>
  ) {
    this.isEditing = false;
  }

  ngOnInit() {
    this.initiateView();
  }

  ngOnChanges() {
    this.initiateView();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  initiateView() {
    this.pristineGroup = null;
    this.isEditing = false;

    this.meteringPointGroupsView = this.meteringPointGroups
      .filter(mpg => mpg.meteringType === this.paneType)
      .map(meteringPointGroup => {
        return {
          ...meteringPointGroup,
          selected: false,
          meteringPoints: [...meteringPointGroup.meteringPoints.map(mp => ({ ...mp, selected: false }))]
        };
      });
  }

  unsavedChanges(mpg: MeteringPointGroup): boolean {
    let modified = mpg.meteringPoints.some(x => x.selected) || mpg.meteringPoints.some(x => x.added);
    let newName = this.meteringPointGroupsView.find(x => x.id === mpg.id).name;
    let oldName = this.meteringPointGroups.find(x => x.id === mpg.id).name;
    return modified || newName !== oldName;
  }

  selectMeteringPointToRemove(mp: MeteringPoint) {
    mp.selected = !mp.selected;
  }

  selectMeteringPoints(mpg: MeteringPointGroup) {
    let existingMeteringPointIds = this.getMeteringPointIds(mpg);
    let dialogRef = this.sxDialogService.openDialog(MeteringPointFormComponent, {
        meteringType: this.paneType,
        meteringPointIds: existingMeteringPointIds
      });

      this.subs.sink = dialogRef.afterClosed().subscribe((meteringPoints: MeteringPoint[]) => {
        let allSelectedMeteringPoint = [...mpg.meteringPoints, ... meteringPoints.map( mp => 
          <MeteringPoint>{ ...mp, selected: false, added: true
          })];
        this.meteringPointGroupsView.find(x => x.id === mpg.id).meteringPoints = allSelectedMeteringPoint;
        this.changeDetectorRef.detectChanges();
      });
  }

  getMeteringPointIds(meteringPointGroup: MeteringPointGroup) {
    let mpIds: number[] = [];
    if (meteringPointGroup.meteringPoints !== null && meteringPointGroup.meteringPoints.length !== 0) {
      meteringPointGroup.meteringPoints.forEach(a => {
        if (a.id !== null) {
          mpIds.push(a.id);
        }
      });
    }
    return mpIds;
  }

  removeGroup(mpg: MeteringPointGroup) {
    const dialogRef = this.sxDialogService.confirmDialog(
      this.translate.instant('metering.group-pane.remove_group'),
      this.translate.instant('metering.group-pane.remove_group_button')
    );

    this.subs.sink = dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        if (this.isCurrentFilterGroup(mpg)) {
          this.sxSnackbarService.openSnackBar(this.translate.instant('metering.group-pane.no_filter'));
          this.updateMeteringPointFilter(mpg);
        }

        this.store.dispatch(new fromMeteringStore.RemoveMeteringPointGroup(mpg));
      }

      mpg = { ...mpg, selected: false };
      this.isEditing = false;
    });
  }

  editGroup(mpg: MeteringPointGroup) {
    this.initiateView();
    mpg = this.meteringPointGroupsView.find(x => x.id === mpg.id);
    mpg.selected = true;
    this.pristineGroup = { ...mpg };
    this.isEditing = true;
  }

  cancelEditing(mpg: MeteringPointGroup) {
    mpg.selected = false;
    this.initiateView();
  }

  addNewGroup() {
    let dialogRef = this.sxDialogService.openDialog(MeteringGroupDialogComponent, {
      meteringType: this.paneType
    });

    this.subs.sink = dialogRef
      .afterClosed()
      .pipe(filter(x => !!x))
      .subscribe((newGroup: MeteringPointGroup) => {
        this.meteringPointGroupsView = [newGroup, ...this.meteringPointGroupsView];
        this.sxSnackbarService.openSnackBar(this.translate.instant('metering.group-form.create_confirmation'), '');
      });
  }

  saveEditing(mpg: MeteringPointGroup) {
    mpg = { ...mpg, meteringPoints: mpg.meteringPoints.filter(mp => !mp.selected) };
    mpg.selected = false;
    this.isEditing = false;

    if (!mpg.id || mpg.id <= 0) {
      return;
    }

    this.store.dispatch(new fromMeteringStore.UpdateMeteringPointGroup(mpg));

    // In case all metering points are removed from the current filter
    if (mpg.meteringPoints.length === 0 && this.isCurrentFilterGroup(mpg)) {
      this.sxSnackbarService.openSnackBar(this.translate.instant('metering.group-form.save_changes_no_mps'));
      this.updateMeteringPointFilter(mpg);
      return;
    }
    // Standard message
    this.sxSnackbarService.openSnackBar(this.translate.instant('metering.group-form.save_changes'));
  }

  isCurrentFilterGroup(mpg: MeteringPointGroup): boolean {
    if (!this.userSettings) {
      return false;
    }

    if (
      this.userSettings.coldMeteringFilter &&
      this.userSettings.coldMeteringFilter.filterType === MeteringFilterType.MeteringPointGroup &&
      this.userSettings.coldMeteringFilter.filterId === mpg.id
    ) {
      return true;
    }

    if (
      this.userSettings.hotMeteringFilter &&
      this.userSettings.hotMeteringFilter.filterType === MeteringFilterType.MeteringPointGroup &&
      this.userSettings.hotMeteringFilter.filterId === mpg.id
    ) {
      return true;
    }

    return false;
  }

  updateMeteringPointFilter(mpg: MeteringPointGroup) {
    let newFilter = {
      ...(mpg.meteringType === MeteringType.Cold
        ? this.userSettings.coldMeteringFilter
        : this.userSettings.hotMeteringFilter),
      filterId: -1,
      filterType: MeteringFilterType.Unfiltered,
      meteringType: mpg.meteringType
    };

    this.userSettings = {
      ...this.userSettings,
      coldMeteringFilter: mpg.meteringType === MeteringType.Cold ? newFilter : this.userSettings.coldMeteringFilter,
      hotMeteringFilter: mpg.meteringType === MeteringType.Hot ? newFilter : this.userSettings.hotMeteringFilter
    };

    this.accountStore.dispatch(new fromAccountStore.SettingUpdate({userSettings: this.userSettings,userId:this.userInfo.id, filterArea:this.filterArea, meteringType : mpg.meteringType, meteringPoints:null}));
  }
}
